import { createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ui, cn } from '../../lib/ui';
import {
  isSafeLink,
  pageHref,
  paletteVars,
  paragraphs,
  upcomingEvents,
  visiblePages,
} from '../../lib/sites';
import SitePattern from './SitePattern';

/**
 * Renders an organization's template site (docs/Decisions.md D43): the public
 * page at /s/:slug and the live preview in the site builder share it.
 *
 * Every colour is a design-system utility. The org's palette is content, so
 * it is applied as CSS custom properties on this root (paletteVars), which
 * re-themes the zone and ink utilities underneath: `bg-z-accent` becomes the
 * org's accent, `text-z-on-accent` its computed readable label colour,
 * `bg-dcon-deep` its band. Content blocks are plain text rendered as text.
 *
 * Props:
 *   site      { template, pattern, palette: { tokens }, siteTitle, tagline, pages }
 *   orgName, slug, logoUrl, hostedUrl
 *   pageId    the page to show
 *   events    the org's public events (for the events page and the community home)
 *   onNavigate(pageId)  preview mode: page links call this instead of routing
 *   basePath  where the pages live: `/s/<slug>` by default, '' on the org's
 *             own website host (docs/Decisions.md D60 item 4)
 *   appOrigin where app pages (event details) live when they are not on this
 *             host, for example `https://portal.example.org`
 */

const SiteLinks = createContext({ basePath: undefined, appOrigin: '' });

/** An app page: a router link on this host, a plain link to the portal otherwise. */
function AppLink({ to, className, children }) {
  const { appOrigin } = useContext(SiteLinks);
  if (appOrigin) {
    return (
      <a className={className} href={`${appOrigin}${to}`}>
        {children}
      </a>
    );
  }
  return (
    <Link className={className} to={to}>
      {children}
    </Link>
  );
}

function formatEventDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function PageLink({ slug, pageId, onNavigate, className, current, children }) {
  const { basePath } = useContext(SiteLinks);
  if (onNavigate) {
    return (
      <button
        type="button"
        className={className}
        aria-current={current ? 'page' : undefined}
        onClick={() => onNavigate(pageId)}
      >
        {children}
      </button>
    );
  }
  return (
    <Link
      to={pageHref(slug, pageId, basePath)}
      className={className}
      aria-current={current ? 'page' : undefined}
    >
      {children}
    </Link>
  );
}

function Blocks({ blocks, layout }) {
  if (!blocks || !blocks.length) return null;
  const grid =
    layout === 'two'
      ? cn(ui.grid, ui.gridMd2)
      : layout === 'three'
        ? cn(ui.grid, ui.gridMd3)
        : 'flex flex-col gap-4';
  return (
    <div className={grid}>
      {blocks.map((block, i) => (
        <section key={i} className={ui.cardFlat}>
          {block.heading ? <h2 className={ui.h2}>{block.heading}</h2> : null}
          {paragraphs(block.body).map((p, j) => (
            <p key={j} className="mb-3 whitespace-pre-line text-body text-dcon-ink-body last:mb-0">
              {p}
            </p>
          ))}
          {block.linkLabel && isSafeLink(block.linkUrl) ? (
            <a
              className={cn(ui.btn, 'mt-4 bg-z-accent text-z-on-accent hover:bg-z-accent-strong')}
              href={block.linkUrl}
              rel="noopener noreferrer"
              target={block.linkUrl.startsWith('mailto:') ? undefined : '_blank'}
            >
              {block.linkLabel}
            </a>
          ) : null}
        </section>
      ))}
    </div>
  );
}

function EventList({ events, slug, state }) {
  if (state === 'loading') return <p className={ui.muted}>Loading upcoming events.</p>;
  if (state === 'error')
    return <p className={ui.muted}>Upcoming events could not be loaded right now.</p>;
  const upcoming = upcomingEvents(events);
  if (!upcoming.length)
    return <p className={ui.muted}>No upcoming public events right now. Check back soon.</p>;
  return (
    <ul className={cn(ui.grid, ui.gridMd2)}>
      {upcoming.map(event => (
        <li key={event.eventId} className={ui.cardFlat}>
          <p className={ui.eyebrow}>{formatEventDate(event.startsAt)}</p>
          <h3 className={cn(ui.h3, 'mt-1')}>{event.name}</h3>
          {event.venue ? <p className="text-body-s text-dcon-ink-muted">{event.venue}</p> : null}
          {slug ? (
            <AppLink
              className={cn(ui.btnSecondary, ui.btnSm, 'mt-3')}
              to={`/o/${encodeURIComponent(slug)}/events/${encodeURIComponent(event.eventId)}`}
            >
              Event details
            </AppLink>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function Logo({ logoUrl, orgName, size }) {
  if (!logoUrl) return null;
  return (
    <img
      src={logoUrl}
      alt={`${orgName} logo`}
      className={cn(
        'rounded-dcon-md bg-dcon-surface object-contain p-1',
        size === 'lg' ? 'size-20' : 'size-12'
      )}
    />
  );
}

export default function SiteTemplate({
  site,
  orgName,
  slug,
  logoUrl,
  hostedUrl,
  pageId = 'home',
  events,
  eventsState,
  onNavigate,
  basePath,
  appOrigin = '',
}) {
  const template = site?.template || 'classic';
  const pages = site?.pages || {};
  const nav = visiblePages(pages);
  const page = pages[pageId] || pages.home || { title: '', blocks: [] };
  const title = site?.siteTitle || orgName || '';
  const showEvents = pageId === 'events' || (template === 'community' && pageId === 'home');

  const navLinks = (
    <nav aria-label={`${title} pages`}>
      <ul className={cn('flex flex-wrap gap-2', template === 'classic' ? 'justify-center' : '')}>
        {nav.map(id => (
          <li key={id}>
            <PageLink
              slug={slug}
              pageId={id}
              onNavigate={onNavigate}
              current={id === pageId}
              className={cn(
                'inline-flex min-h-touch cursor-pointer items-center rounded-z-ctl border border-transparent bg-transparent px-3 font-display text-label text-dcon-on-deep no-underline hover:underline',
                id === pageId ? 'underline underline-offset-4' : ''
              )}
            >
              {pages[id]?.title || id}
            </PageLink>
          </li>
        ))}
      </ul>
    </nav>
  );

  let header;
  if (template === 'modern') {
    header = (
      <header className="bg-dcon-deep text-dcon-on-deep">
        <div className={cn(ui.container, 'flex flex-wrap items-center justify-between gap-4 py-3')}>
          <div className="flex items-center gap-3">
            <Logo logoUrl={logoUrl} orgName={orgName} />
            <p className="font-display text-title-s">{title}</p>
          </div>
          {navLinks}
        </div>
      </header>
    );
  } else {
    header = (
      <header className="bg-dcon-deep text-dcon-on-deep">
        <div
          className={cn(
            ui.container,
            'flex flex-col gap-3 py-8',
            template === 'classic' ? 'items-center text-center' : ''
          )}
        >
          <div className={cn('flex items-center gap-4', template === 'classic' ? 'flex-col' : '')}>
            <Logo logoUrl={logoUrl} orgName={orgName} size="lg" />
            <div>
              <p className="font-display text-title-l">{title}</p>
              {site?.tagline ? (
                <p className="mt-1 text-body-l text-dcon-on-deep-muted">{site.tagline}</p>
              ) : null}
            </div>
          </div>
          {navLinks}
        </div>
      </header>
    );
  }

  const layout = template === 'modern' ? 'two' : template === 'community' ? 'three' : 'one';

  return (
    <SiteLinks.Provider value={{ basePath, appOrigin }}>
      <div
        data-zone="member"
        style={paletteVars(site?.palette?.tokens)}
        className={cn(
          'relative flex flex-col bg-z-canvas text-dcon-ink-body',
          onNavigate ? '' : 'min-h-screen'
        )}
      >
        {header}
        <main className="relative flex-1">
          <SitePattern pattern={site?.pattern} />
          <div className={cn(ui.container, 'relative flex flex-col gap-6 py-10')}>
            {template === 'modern' && pageId === 'home' ? (
              <div className="py-6">
                <h1 className="text-display text-dcon-ink">{title}</h1>
                {site?.tagline ? (
                  <p className="mt-3 max-w-prose text-body-l text-dcon-ink-muted">{site.tagline}</p>
                ) : null}
              </div>
            ) : (
              <h1 className={cn(ui.h1, 'text-dcon-ink')}>{page.title}</h1>
            )}
            <Blocks blocks={page.blocks} layout={layout} />
            {showEvents ? (
              <section aria-label="Upcoming events" className="flex flex-col gap-3">
                {pageId !== 'events' ? <h2 className={ui.h2}>Upcoming events</h2> : null}
                <EventList events={events} slug={slug} state={eventsState} />
              </section>
            ) : null}
          </div>
        </main>
        <footer className="bg-dcon-deep text-dcon-on-deep">
          <div
            className={cn(
              ui.container,
              'flex flex-wrap items-center justify-between gap-3 py-6 text-body-s'
            )}
          >
            <p>{orgName}</p>
            {hostedUrl ? (
              <a className="text-dcon-on-deep underline" href={hostedUrl} rel="noopener noreferrer">
                Visit our full website
              </a>
            ) : null}
            {appOrigin ? (
              <a
                className="text-dcon-on-deep underline"
                href={`${appOrigin}/o/${encodeURIComponent(slug)}/login`}
              >
                Member sign in
              </a>
            ) : null}
          </div>
        </footer>
      </div>
    </SiteLinks.Provider>
  );
}
